/**
 * V70 Phase C — 참가자 대시보드 (게시된 대진표 목록)
 *
 * - listPublishedBrackets Callable 로 공개 대진표 메타 조회
 * - 각 카드 클릭 시 /tournament/bracket/:bracketId 로 이동
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import RichContentViewer from '../../shared/components/RichContentViewer';
import RichTextEditor from '../../shared/components/RichTextEditor';
import { useAnnouncementUpload, PopupDismissPolicy } from '../../shared/hooks/useAnnouncements';
import { BracketStatus, statusColor, statusLabel, tsToMs, TsLike } from './shared/bracketTypes';
// V70G8B — 일반 공지와 동일한 팝업 설정 UI 재사용
import {
  PopupSettingsSection,
  PopupFormState,
  INITIAL_POPUP_FORM_STATE,
  buildPopupPayload,
  isoToKstDate,
  validatePopupForm,
} from '../announcements/PopupSettingsSection';
import { popupDetailKey, useHoldPopups } from '../../shared/hooks/popupHold';
import { fmtKstDateTime } from './shared/kstFormat';
import { fetchAllAnnouncements, ANNOUNCEMENT_PAGE_MAX, AnnouncementPage } from './shared/announcementListAll';
import { invalidateQuery, peekQuery, setQueryData, useTournamentQuery } from './shared/tournamentQueryCache';
// V70-J — 게임대회 공통 헤더
import { TournamentPageHeader, tournamentPageStyle } from './shared/TournamentPageHeader';
import { tournamentAuthorLabel } from './shared/authorLabel';
// V70-L — 풀 다크모드 1차 시범 (대시보드 전용 토큰 — 다른 게임대회 페이지는 V70-K 네이비 유지)
import {
  DARK, DARK_STATUS, DARK_RICH_CLASS, DARK_RICH_CSS, darkCard, darkSectionTitle, darkRow, darkPinnedRow, darkHighlight,
  darkPrimaryBtn, darkSecondaryBtn, darkDangerBtn, darkErrorBox, darkEmptyBox, darkModalOverlay, darkModalBox,
  darkTintBadge,
} from './shared/tournamentDarkTheme';
// V70-N — 모달을 사이드바 제외 본문 영역 중앙에 (AppShell 실제 레이아웃 측정)
import { contentCenteredOverlay, useAppShellContentInsets } from './shared/useAppShellContentInsets';

interface BracketListItem {
  bracketId: string;
  sourceFormId: string;
  sourceFormTitle: string;
  // GC-DASH-01 (2026-10-23) — 양식 대표 이미지. 미등록 시 null → FE 플레이스홀더.
  sourceFormCoverImageUrl?: string | null;
  status: BracketStatus;
  participantCount: number;
  publishedAt: TsLike;
  championPublicParticipantId: string | null;
  championDisplayName: string | null;
  // V70 QA 추가 수정 #4 (2026-10-21) — 단체전/개인전 분기용.
  isTeam?: boolean;
}

export function TournamentDashboardPage() {
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useCurrentUser();
  // V70 QA 통합 공지 (2026-10-23) — 이벤트별 BoardPostsModal 상태 제거 (전역 공지 섹션으로 대체).
  const uid = user?.profile?.id ?? null;
  const isAdmin = !!user?.roles?.includes('system_admin');

  // V70-H — 탭 재진입 시 이전 결과를 바로 표시 (30초 지나면 화면 유지한 채 뒤에서 재조회). [새로고침]은 강제 재조회.
  const bracketsQuery = useTournamentQuery<BracketListItem[]>(uid ? `dash:brackets:${uid}` : null, async () => {
    try {
      const fns = getFunctions();
      const r = await httpsCallable<Record<string, never>, { brackets: BracketListItem[] }>(fns, 'listPublishedBrackets')({});
      return r.data.brackets || [];
    } catch (err) {
      console.warn('[TournamentDashboard] fetch failed:', err);
      throw err;
    }
  });
  const brackets = bracketsQuery.data ?? [];
  const loading = bracketsQuery.loading;
  const hasError = !!bracketsQuery.error;
  const reload = () => { void bracketsQuery.reload(); };

  if (userLoading) return <div style={{ padding: 24 }}>로딩 중…</div>;
  if (!user) return <div style={{ padding: 24 }}>로그인이 필요합니다.</div>;

  const fmtKst = (ms: number): string => {
    if (!ms) return '-';
    const d = new Date(ms + 9 * 3600 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} KST`;
  };

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {/* V70-J — e스포츠 공통 헤더 (기존 [새로고침] 버튼은 헤더 오른쪽으로 이동, 동작 동일) */}
        {/* V70-L — 다크 톤 헤더 + 본문 다크 카드. 공지·결과 본문 다크 표시용 범위 한정 스타일 */}
        <style>{DARK_RICH_CSS}</style>
        <TournamentPageHeader
          page="dashboard"
          tone="dark"
          actions={<button style={darkSecondaryBtn} onClick={reload}>새로고침</button>}
        />

        {hasError && (
          <div style={darkErrorBox}>대진표를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</div>
        )}

        {/* V70 QA 통합 공지 (2026-10-23) — 게임센터 전역 공지사항 섹션 (최상단). */}
        <AnnouncementsSection isAdmin={isAdmin} uid={user.profile.id} />

        {/* V70 QA 신규 기능 #3 (2026-10-22) — 중계 예정 섹션 */}
        <UpcomingBroadcastsSection uid={user.profile.id} />

        {/* V70 QA 리오픈 #5 (2026-10-22) — 결과 공지 섹션 (대진표 없이 게시된 결과) */}
        <ResultAnnouncementsListSection uid={user.profile.id} />

        {/* GC-DASH-01 (2026-10-23) — "대진표 보기" 섹션:
              이미지 중심 카드 레이아웃. 카드 전체 클릭 → 즉시 /tournament/bracket/:id 이동.
              "대진표 보기 →" 버튼 제거. 중간 모달/목록 없음.
              이미지 미등록 시 플레이스홀더 (그라데이션 블록 + 아이콘). */}
        <section style={{ marginBottom: 16 }}>
          {/* V70-K — 이 섹션 제목만 카드 밖(배경 위)에 있어 밝은 색 */}
          <h2 style={{ ...darkSectionTitle, margin: '0 0 10px' }}>🏆 대진표 보기</h2>
          {loading && <div style={placeholderStyle}>로딩 중…</div>}
          {!loading && !hasError && brackets.length === 0 && (
            <div style={placeholderStyle}>현재 게시된 대진표가 없습니다. 관리자가 공개하면 이곳에 노출됩니다.</div>
          )}
          {!loading && brackets.length > 0 && (
            <div style={bracketGridStyle}>
              {brackets.map((b) => (
                <div
                  key={b.bracketId}
                  style={bracketCardStyle}
                  onClick={() => navigate(`/tournament/bracket/${b.bracketId}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/tournament/bracket/${b.bracketId}`);
                    }
                  }}
                >
                  {/* 이미지 영역 — 양식 coverImageUrl 또는 플레이스홀더 */}
                  <div style={bracketCardImageWrap}>
                    {b.sourceFormCoverImageUrl ? (
                      <img
                        src={b.sourceFormCoverImageUrl}
                        alt={b.sourceFormTitle}
                        style={bracketCardImageStyle}
                        loading="lazy"
                      />
                    ) : (
                      <div style={bracketCardPlaceholderStyle} aria-hidden="true">
                        <span style={{ fontSize: 42, lineHeight: 1 }}>🏆</span>
                      </div>
                    )}
                    {/* 상태 뱃지 overlay — V70-M: 의미 색 유지 + 대비 보정 (불투명 다크 틴트라 이미지 위에서도 읽힘) */}
                    <span style={{
                      ...badge, position: 'absolute', top: 8, right: 8,
                      ...darkTintBadge(statusColor(b.status)),
                    }}>
                      {statusLabel(b.status)}
                    </span>
                  </div>
                  {/* 하단 메타 */}
                  <div style={{ padding: 14 }}>
                    {/* 양식 제목 — 사용자 지시: title 을 "게임명" 으로 무조건 간주하지 않고 양식 제목 그대로 노출.
                        게임명 분리 필드는 데이터 보완안으로 별도 보고 (현재 범위 밖). */}
                    {/* V70-G — 넓어진 카드에서 제목이 잘리지 않도록 말줄임 대신 줄바꿈 */}
                    <h3 style={{
                      margin: 0, fontSize: 15, fontWeight: 700, color: DARK.text, lineHeight: 1.4,
                      overflowWrap: 'anywhere',
                    }}>{b.sourceFormTitle}</h3>
                    <div style={{ marginTop: 6, fontSize: 11, color: DARK.textMuted }}>
                      참가자 {b.participantCount}{b.isTeam ? '팀' : '명'} · 게시 {fmtKst(tsToMs(b.publishedAt))}
                    </div>
                    {b.championDisplayName && (
                      <div style={{
                        ...darkHighlight, marginTop: 8, padding: '6px 8px', borderRadius: 6,
                        fontSize: 12, overflowWrap: 'anywhere', color: DARK.text,
                      }}>
                        🏆 우승: <strong>{b.championDisplayName}</strong>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* V70 QA 통합 공지 (2026-10-23) — BoardPostsModal 제거. AnnouncementsSection 로 대체. */}
      </div>
    </div>
  );
}

// V70 QA 신규 기능 #3 (2026-10-22) — 중계 예정 섹션.
//   listUpcomingBroadcasts 호출 + 30초 interval 로 자동 refresh (시간 경과 자동 반영).
function UpcomingBroadcastsSection({ uid }: { uid: string }) {
  // V70-H — 재진입 시 이전 결과 즉시 표시 (조회 실패 시 기존과 같이 빈 목록)
  const { data, loading, reload } = useTournamentQuery<any[]>(`dash:broadcasts:${uid}`, async () => {
    const fns = getFunctions();
    const r = await httpsCallable<Record<string, never>, { broadcasts: any[] }>(fns, 'listUpcomingBroadcasts')({});
    return r.data.broadcasts || [];
  });
  const items = data ?? [];

  // 30초 interval 로 재조회 (종료된 중계 자동 제외 + 신규 반영)
  useEffect(() => {
    const t = setInterval(() => { void reload(); }, 30 * 1000);
    return () => clearInterval(t);
  }, [reload]);

  // V70G8J — MM/DD → YYYY-MM-DD (하단 대진표 카드 게시일과 같은 형식, KST 계산 동일)
  const fmtKstHM = fmtKstDateTime;

  if (loading) return null;
  return (
    <section style={{ ...darkCard, marginBottom: 16, padding: 14, color: DARK.text }}>
      {/* V70 QA 리오픈 NNN6 (2026-10-23) — 명칭 "중계 예정" → "중계" (진행 중/예정 모두 포함).
            빈 상태 메시지도 "중계 일정이 없습니다" 로 일관. 내부 state/필드명/API 는 변경 없음. */}
      {/* V70G5A Bug 5 — 대시보드 4섹션 타이틀 통일 (공지사항/대진표 보기/중계/결과 공지):
            h2 · fontSize 16 · fontWeight 700 · color #1e293b. */}
      <h2 style={darkSectionTitle}>🎥 중계</h2>
      {items.length === 0 ? (
        <div style={{ marginTop: 8, fontSize: 12, color: DARK.textMuted }}>중계 일정이 없습니다.</div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((b: any) => (
            <li key={b.matchId} style={{ ...darkRow, padding: 10, fontSize: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* V70 QA 신규 기능 #3 추가 검증 (2026-10-22) — 예정/LIVE 상태 구분 뱃지.
                    V70G5A Bug 7/8 (2026-10-27) — matchStatus 기반 '완료' 뱃지 추가.
                      - matchStatus=completed/bye → '완료' (BE isLive 는 false 로 보장)
                      - isLive=true → 'LIVE' (매치 미완료 + broadcast 창 안)
                      - 그 외 (매치 미완료 + 창 밖) → '예정' */}
                {(b.matchStatus === 'completed' || b.matchStatus === 'bye') ? (
                  <span style={{ ...DARK_STATUS.done, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>🏁 완료</span>
                ) : b.isLive ? (
                  <span style={{ ...DARK_STATUS.live, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>🔴 LIVE</span>
                ) : (
                  <span style={{ ...DARK_STATUS.scheduled, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>⏱ 예정</span>
                )}
                <strong>{b.sourceFormTitle}</strong>
                {/* V70 QA 리오픈 NNN7 (2026-10-23) — "R{roundNo}" → BE 가 계산한 roundLabel (16강/8강/4강/준결승/결승 등).
                      BE fallback: roundLabel 미포함 (예: 구버전 응답) 시 FE 자체 계산. */}
                <span style={{ color: DARK.textMuted }}>
                  {(b as any).roundLabel || (((b as any).totalRounds && b.roundNo)
                    ? (() => {
                        const tr = (b as any).totalRounds as number;
                        const remaining = tr - b.roundNo + 1;
                        if (remaining === 1) return '결승';
                        if (remaining === 2) return '준결승';
                        return `${Math.pow(2, remaining)}강`;
                      })()
                    : `R${b.roundNo}`)} · {b.bestOf}판 {Math.ceil(b.bestOf / 2)}승제
                </span>
              </div>
              <div style={{ marginTop: 4, color: DARK.text }}>
                {b.playerA.teamName || b.playerA.displayName || '-'} vs {b.playerB.teamName || b.playerB.displayName || '-'}
              </div>
              <div style={{ marginTop: 2, color: DARK_STATUS.scheduled.color, fontSize: 11 }}>
                {fmtKstHM(tsToMs(b.broadcast.startAt))} ~ {fmtKstHM(tsToMs(b.broadcast.endAt))} KST · @{b.broadcast.location}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// V70 QA 리오픈 #5 (2026-10-22) — 결과 공지 섹션 (브래킷 없이 게시된 결과만).
function ResultAnnouncementsListSection({ uid }: { uid: string }) {
  const [selected, setSelected] = useState<any | null>(null);
  // V70-H — 재진입 시 이전 결과 즉시 표시 (조회 실패 시 기존과 같이 섹션 미표시)
  const { data, loading } = useTournamentQuery<any[]>(`dash:results:${uid}`, async () => {
    const fns = getFunctions();
    const r = await httpsCallable<Record<string, never>, { announcements: any[] }>(fns, 'listResultAnnouncements')({});
    return r.data.announcements || [];
  });
  const items = data ?? [];

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section style={{ ...darkCard, marginBottom: 16, padding: 14, color: DARK.text }}>
      {/* V70G5A Bug 5 — 대시보드 4섹션 타이틀 통일 (공지사항/대진표 보기/중계/결과 공지). V70-L 다크 */}
      <h2 style={darkSectionTitle}>🏁 결과 공지</h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((a: any) => (
          <li key={a.sourceFormId} style={{ ...darkRow, padding: 10, fontSize: 12, cursor: 'pointer' }} onClick={() => setSelected(a)}>
            <div style={{ fontWeight: 600 }}>{a.title}</div>
            <div style={{ fontSize: 11, color: DARK.textMuted, marginTop: 2 }}>
              {a.winnerName ? `🏆 ${a.winnerKind === 'team' ? '우승팀' : '우승자'}: ${a.winnerName} · ` : ''}
              {/* V70-O — 수정일시(updatedAt) 대신 최초 작성일시(createdAt). createdAt 없는 옛 문서만 updatedAt 로 대체 */}
              {tournamentAuthorLabel(a.createdByName)} · {resultCreatedLabel(a)}
            </div>
          </li>
        ))}
      </ul>
      {selected && (
        <ResultDetailOverlay onClose={() => setSelected(null)}>
          <div style={{ ...darkModalBox, maxWidth: 720, maxHeight: '85vh' }} onClick={(e) => e.stopPropagation()} data-testid="result-detail-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: DARK.text }}>🏁 {selected.title}</h3>
              <button style={darkSecondaryBtn} onClick={() => setSelected(null)}>닫기</button>
            </div>
            {selected.winnerName && (
              <div style={{ ...darkHighlight, padding: 8, fontSize: 13, marginBottom: 10, color: DARK.text }}>
                🏆 {selected.winnerKind === 'team' ? '우승팀' : '우승자'}: <strong>{selected.winnerName}</strong>
              </div>
            )}
            {/* V70 QA 리오픈 #10/#12 (2026-10-22) — RichContentViewer 재사용 (ul/ol/table/img 스타일 포함) */}
            <RichContentViewer content={selected.contentHtml || ''} format="html" className={DARK_RICH_CLASS} style={{ fontSize: 13, lineHeight: 1.6, color: DARK.text }} />
          </div>
        </ResultDetailOverlay>
      )}
    </section>
  );
}

// V70-O — 결과 공지 목록의 표시 일시: 최초 작성일시(기존과 같은 표기 형식)
export function resultCreatedLabel(a: { createdAt?: any; updatedAt?: any }): string {
  const ms = tsToMs(a.createdAt) || tsToMs(a.updatedAt);
  return new Date(ms).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

// V70-N — 결과 공지 상세 오버레이 (열려 있을 때만 본문 영역 측정)
function ResultDetailOverlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const insets = useAppShellContentInsets();
  return (
    <div style={contentCenteredOverlay(darkModalOverlay, insets)} onClick={onClose} data-testid="result-detail-overlay">
      {children}
    </div>
  );
}

// V70 QA 통합 공지 (2026-10-23 → 2026-10-23 재검토) — 게임센터 전역 공지사항 섹션.
//   최상단에 표시, 중요 공지(isPinned) 상단 고정. V70-F — 기본 상태에서 전체 공지 표시 (최신 5건 요약 제거).
//   Admin 에게 [글쓰기] 버튼 + 상세에서 [수정]/[삭제] 노출.
//   Admin 단방향 게시판: 댓글 기능 제거 (사용자 지시 2026-10-23 재검토).
interface AnnouncementSummary {
  announcementId: string;
  title: string;
  isPinned: boolean;
  createdAt: any;
  createdBy: string;
  createdByName: string;
  createdByOrgUnit: string | null;
  updatedAt: any | null;
}
interface AnnouncementFull extends AnnouncementSummary {
  contentHtml: string;
  updatedBy: string | null;
  updatedByName: string | null;
  // V70G8B — 팝업 설정 (팝업이 아닌 공지는 필드 없음)
  isPopup?: boolean;
  popupStartAt?: TsLike | null;
  popupEndAt?: TsLike | null;
  popupDismissPolicy?: PopupDismissPolicy;
  popupPriority?: number;
  popupRevision?: number;
}

/** V70G8B — 서버 공지 문서의 팝업 필드 → 폼 상태 */
export function popupFormFromAnnouncement(a: AnnouncementFull | null): { form: PopupFormState; wasPopup: boolean } {
  if (!a || a.isPopup !== true) {
    return { form: INITIAL_POPUP_FORM_STATE, wasPopup: false };
  }
  const toIso = (t: TsLike | null | undefined) => {
    const ms = tsToMs(t ?? null);
    return ms ? new Date(ms).toISOString() : null;
  };
  return {
    wasPopup: true,
    form: {
      isPopup: true,
      startDate: isoToKstDate(toIso(a.popupStartAt)),
      endDate: isoToKstDate(toIso(a.popupEndAt)),
      dismissPolicy: a.popupDismissPolicy || INITIAL_POPUP_FORM_STATE.dismissPolicy,
      priority: String(a.popupPriority ?? INITIAL_POPUP_FORM_STATE.priority),
      bumpRevision: false,
    },
  };
}

function AnnouncementsSection({ isAdmin, uid }: { isAdmin: boolean; uid: string }) {
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState<null | { mode: 'create' } | { mode: 'edit'; announcement: AnnouncementFull }>(null);

  // V70G8B — 팝업 '상세 보기' 진입 (?announcement=ID) 시 해당 공지 상세를 연다. 1회 소비 후 파라미터 제거.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedAnnouncementId = searchParams.get('announcement');
  useEffect(() => {
    if (!linkedAnnouncementId) return;
    setSelectedId(linkedAnnouncementId);
    const next = new URLSearchParams(searchParams);
    next.delete('announcement');
    setSearchParams(next, { replace: true });
  }, [linkedAnnouncementId, searchParams, setSearchParams]);

  // V70-F — 기본 상태에서 공지 전체 표시 (기존 "최신 5건 + [전체 보기]" 토글 제거).
  //   모든 페이지를 이어서 조회 (페이지당 최대 100건, 서버 정렬 그대로: 고정 → 최신순, ID 기준 중복 제거).
  //   [더 보기]는 안전 상한(50페이지) 도달 등으로 커서가 남은 경우에만 이어서 조회용으로 노출된다.
  //   목록에 내부 스크롤·고정 높이 없음 → 대시보드 페이지 세로 스크롤로 열람.
  const pageLimit = ANNOUNCEMENT_PAGE_MAX;

  // V70-H — 목록 캐시: 탭 재진입 시 즉시 표시, 상세 닫기만으로는 재조회하지 않음.
  //   공지 저장·삭제 후에만 강제 재조회. 재조회 중에도 기존 목록은 그대로 보임 (목록이 사라졌다 나타나는 현상 제거).
  const listKey = `dash:ann:${uid}`;
  const annQuery = useTournamentQuery<AnnouncementPage<AnnouncementSummary>>(listKey, async () => {
    const fns = getFunctions();
    const call = httpsCallable<{ limit: number; cursor?: string }, { announcements: AnnouncementSummary[]; nextCursor: string | null }>(
      fns, 'listGameCenterAnnouncements'
    );
    return fetchAllAnnouncements<AnnouncementSummary>(async (req) => (await call(req)).data);
  });
  const items = annQuery.data?.announcements ?? [];
  const nextCursor = annQuery.data?.nextCursor ?? null;
  const loading = annQuery.loading;
  const detailKey = (id: string) => `dash:annDetail:${uid}:${id}`;
  const refreshAfterChange = (changedId?: string | null) => {
    if (changedId) invalidateQuery(detailKey(changedId));
    void annQuery.reload();
  };

  // 사용자 지시 "nextCursor 기반 페이지네이션 실제 연결" — 더 보기 핸들러:
  //   현재 items 끝의 cursor 로 추가 fetch → 기존 목록 뒤에 이어 붙임.
  //   중복 방지: announcementId 기준 Set 체크.
  //   V70-H — 결과는 목록 캐시에 반영. 조회 중 목록이 새로 바뀌었으면(저장·삭제 후 재조회) 이전 커서 결과는 버림.
  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    const base = annQuery.data;
    setLoadingMore(true);
    try {
      const fns = getFunctions();
      const r = await httpsCallable<{ limit: number; cursor: string }, { announcements: AnnouncementSummary[]; nextCursor: string | null }>(
        fns, 'listGameCenterAnnouncements'
      )({ limit: pageLimit, cursor: nextCursor });
      if (!base || peekQuery<AnnouncementPage<AnnouncementSummary>>(listKey)?.data !== base) return;
      const incoming = r.data.announcements || [];
      const seen = new Set(base.announcements.map((p) => p.announcementId));
      const deduped = incoming.filter((a) => !seen.has(a.announcementId));
      setQueryData<AnnouncementPage<AnnouncementSummary>>(listKey, {
        announcements: [...base.announcements, ...deduped],
        nextCursor: r.data.nextCursor || null,
      });
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  const fmtKstDate = (ms: number): string => {
    if (!ms) return '-';
    const d = new Date(ms + 9 * 3600 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  };

  return (
    <section style={{ ...darkCard, marginBottom: 16, padding: 16, color: DARK.text }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h2 style={darkSectionTitle}>📢 공지사항</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {isAdmin && (
            <button
              style={darkPrimaryBtn}
              onClick={() => setEditorOpen({ mode: 'create' })}
            >✏️ 글쓰기</button>
          )}
        </div>
      </div>
      {loading && <div style={{ fontSize: 13, color: DARK.textMuted }}>로딩 중…</div>}
      {!loading && items.length === 0 && (
        <div style={{ ...darkEmptyBox, padding: 20, background: DARK.surface, borderRadius: 8 }}>
          등록된 공지사항이 없습니다.
        </div>
      )}
      {!loading && items.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((a) => (
            <li
              key={a.announcementId}
              // V70-L 2차 — 중요공지 여부는 데이터 필드(isPinned)로만 판단 (제목 문자열 무관). 해제 시 일반 행으로 즉시 복귀
              style={{ ...(a.isPinned === true ? darkPinnedRow : darkRow), padding: 12, cursor: 'pointer' }}
              data-pinned={a.isPinned === true ? 'true' : 'false'}
              onClick={() => setSelectedId(a.announcementId)}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {a.isPinned && (
                  <span style={{ ...DARK_STATUS.pinned, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>📌 중요</span>
                )}
                <strong style={{ color: DARK.text, fontSize: 14, flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</strong>
              </div>
              <div style={{ marginTop: 4, fontSize: 11, color: DARK.textMuted }}>
                {/* V70-O — 최초 작성일만 표시 ('(수정됨)' 제거, updatedAt 데이터는 그대로) */}
                {tournamentAuthorLabel(a.createdByName)} · {fmtKstDate(tsToMs(a.createdAt))}
              </div>
            </li>
          ))}
        </ul>
      )}
      {nextCursor && (
        <div style={{ marginTop: 10, textAlign: 'center' }}>
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            style={{
              ...darkSecondaryBtn,
              padding: '6px 14px',
              opacity: loadingMore ? 0.6 : 1,
              cursor: loadingMore ? 'not-allowed' : 'pointer',
            }}
          >{loadingMore ? '불러오는 중…' : '더 보기'}</button>
        </div>
      )}

      {selectedId && (
        <AnnouncementDetailModal
          announcementId={selectedId}
          cacheKey={detailKey(selectedId)}
          summary={items.find((a) => a.announcementId === selectedId) ?? null}
          isAdmin={isAdmin}
          onClose={() => setSelectedId(null)}
          onDeleted={() => { setSelectedId(null); refreshAfterChange(selectedId); }}
          onEdit={(ann) => { setSelectedId(null); setEditorOpen({ mode: 'edit', announcement: ann }); }}
        />
      )}
      {editorOpen && (
        <AnnouncementEditorModal
          mode={editorOpen.mode}
          existing={editorOpen.mode === 'edit' ? editorOpen.announcement : null}
          onClose={() => setEditorOpen(null)}
          onSaved={() => {
            const editedId = editorOpen.mode === 'edit' ? editorOpen.announcement.announcementId : null;
            setEditorOpen(null);
            refreshAfterChange(editedId);
          }}
        />
      )}
    </section>
  );
}

function AnnouncementDetailModal({ announcementId, cacheKey, summary, isAdmin, onClose, onDeleted, onEdit }: {
  announcementId: string;
  cacheKey: string;
  /** V70-H — 목록에 이미 있는 제목·중요·작성자·작성일. 본문 도착 전 머리말을 바로 표시 (딥링크로 열려 목록에 없으면 null) */
  summary: AnnouncementSummary | null;
  isAdmin: boolean;
  onClose: () => void;
  onDeleted: () => void;
  onEdit: (a: AnnouncementFull) => void;
}) {
  // V70G8C: 게임대회 공지 상세 모달이 열려 있는 동안 다음 팝업 표시 보류
  // V70G8E: 팝업 '상세 보기'/딥링크로 잡아 둔 대기 보류를 이 모달이 넘겨받음 (권한 조회·렌더 지연 공백 없음)
  useHoldPopups(true, popupDetailKey('gameCenter', announcementId));
  const navigate = useNavigate();
  // V70-H — 본문(contentHtml)은 목록에 없으므로 서버 조회 유지.
  //   다시 열면 직전 본문을 바로 보여 주고, 항상 뒤에서 최신본을 다시 확인 (freshMs 0).
  const detail = useTournamentQuery<AnnouncementFull>(cacheKey, async () => {
    const fns = getFunctions();
    const annRes = await httpsCallable<{ announcementId: string }, { announcement: AnnouncementFull }>(
      fns, 'getGameCenterAnnouncement'
    )({ announcementId });
    return annRes.data.announcement;
  }, { freshMs: 0 });
  const ann = detail.data ?? null;
  const loading = detail.loading;
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const loadErr = detail.error ? ((detail.error as { message?: string })?.message || '조회 실패') : null;
  const err = deleteErr || loadErr;
  // 머리말: 상세 응답 우선, 없으면 목록 요약
  const head: AnnouncementSummary | null = ann ?? summary;
  const insets = useAppShellContentInsets(); // V70-N

  const handleDelete = async () => {
    if (!window.confirm('이 공지를 삭제하시겠습니까?')) return;
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'deleteGameCenterAnnouncement')({
        announcementId,
        clientRequestId: `del-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      onDeleted();
    } catch (e: any) {
      setDeleteErr(e?.message || '삭제 실패');
    }
  };

  const fmtKst = (ms: number): string => {
    if (!ms) return '-';
    const d = new Date(ms + 9 * 3600 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  };

  return (
    <div style={contentCenteredOverlay(darkModalOverlay, insets)} onClick={onClose} data-testid="ann-detail-overlay">
      <div style={{ ...darkModalBox, maxWidth: 760, maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()} data-testid="ann-detail-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: '1 1 auto', color: DARK.text }}>
            {head?.isPinned && <span style={{ ...DARK_STATUS.pinned, marginRight: 6, padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>📌 중요</span>}
            {head?.title || (loading ? '로딩 중…' : '공지')}
          </h3>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 10 }}>
            {/* V70-H — 수정·삭제는 서버 최신본 확인 후에만 (다시 연 공지의 이전 본문으로 덮어쓰기 방지) */}
            {isAdmin && ann && !detail.refreshing && (
              <>
                <button style={darkSecondaryBtn} onClick={() => onEdit(ann)}>수정</button>
                <button style={darkDangerBtn} onClick={handleDelete}>삭제</button>
              </>
            )}
            <button style={darkSecondaryBtn} onClick={onClose}>닫기</button>
          </div>
        </div>
        {head && (
          <div style={{ fontSize: 11, color: DARK.textMuted, marginBottom: 10 }}>
            {/* V70-O — 최초 작성일시만 표시 (수정일시 제거) */}
            {tournamentAuthorLabel(head.createdByName)} · {fmtKst(tsToMs(head.createdAt))}
          </div>
        )}
        {err && <div style={darkErrorBox}>{err}</div>}
        {!ann && !err && head && <div style={{ fontSize: 13, color: DARK.textMuted }}>본문 불러오는 중…</div>}
        {/* V70G8H: 본문 내부 링크는 상세를 닫고 SPA 이동 (대상 라우트 가드가 권한 검사) */}
        {ann && (
          <RichContentViewer
            content={ann.contentHtml}
            format="html"
            className={DARK_RICH_CLASS}
            style={{ fontSize: 14, lineHeight: 1.7, color: DARK.text }}
            onInternalLink={(path) => { onClose(); navigate(path); }}
          />
        )}
      </div>
    </div>
  );
}

export function AnnouncementEditorModal({ mode, existing, onClose, onSaved }: {
  mode: 'create' | 'edit';
  existing: AnnouncementFull | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(existing?.title || '');
  const [contentHtml, setContentHtml] = useState(existing?.contentHtml || '');
  const [isPinned, setIsPinned] = useState(existing?.isPinned || false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const insets = useAppShellContentInsets(); // V70-N — 상세 모달과 같은 본문 영역 중앙 (밝은 편집 화면은 그대로)
  // V70G8B — 팝업 설정
  const initialPopup = popupFormFromAnnouncement(mode === 'edit' ? existing : null);
  const [popupForm, setPopupForm] = useState<PopupFormState>(initialPopup.form);
  const wasPopup = initialPopup.wasPopup;
  const { uploadFile } = useAnnouncementUpload();
  const uploadImage = async (file: File): Promise<string> => {
    const att = await uploadFile(file, { trackProgress: false });
    return att.url;
  };

  const handleSave = async () => {
    const t = title.trim();
    if (!t) { setErr('제목을 입력하세요.'); return; }
    const h = contentHtml.trim();
    if (!h) { setErr('본문을 입력하세요.'); return; }
    const popupError = validatePopupForm(popupForm);
    if (popupError) { setErr(popupError); return; }
    const popupPayload = buildPopupPayload(popupForm, wasPopup, mode === 'edit');
    setSaving(true); setErr(null);
    try {
      const fns = getFunctions();
      if (mode === 'create') {
        await httpsCallable(fns, 'createGameCenterAnnouncement')({
          title: t, contentHtml: h, isPinned,
          ...popupPayload,
          clientRequestId: `anc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        });
      } else {
        if (!existing) throw new Error('수정 대상 없음');
        await httpsCallable(fns, 'updateGameCenterAnnouncement')({
          announcementId: existing.announcementId,
          title: t, contentHtml: h, isPinned,
          ...popupPayload,
          clientRequestId: `ancu-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        });
      }
      onSaved();
    } catch (e: any) {
      setErr(e?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={contentCenteredOverlay({ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }, insets)} onClick={onClose} data-testid="ann-editor-overlay">
      <div style={{ background: '#fff', borderRadius: 10, maxWidth: 820, width: '100%', maxHeight: '92vh', overflow: 'auto', padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {mode === 'create' ? '✏️ 공지 작성' : '📝 공지 수정'}
          </h3>
          <button style={{ padding: '4px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 12 }} onClick={onClose}>닫기</button>
        </div>
        {err && <div style={errorBox}>{err}</div>}
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>제목</label>
        <input
          type="text" value={title} onChange={(e) => setTitle(e.target.value)} disabled={saving}
          maxLength={200}
          style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14, boxSizing: 'border-box', marginBottom: 10, fontFamily: 'inherit' }}
        />
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>본문</label>
        <RichTextEditor
          value={contentHtml}
          onChange={setContentHtml}
          onImageUpload={uploadImage}
          disabled={saving}
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 13 }}>
          <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} disabled={saving} />
          <span>📌 중요 공지로 상단 고정</span>
        </label>
        {/* V70G8B — 팝업 설정 (일반 공지와 동일 UI) */}
        <div style={{ marginTop: 14 }} data-testid="gc-popup-settings">
          <PopupSettingsSection
            value={popupForm}
            onChange={setPopupForm}
            isEditMode={mode === 'edit'}
            wasPopup={wasPopup}
          />
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 13 }} onClick={onClose} disabled={saving}>취소</button>
          <button
            style={{ padding: '8px 16px', background: saving ? '#cbd5e1' : '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}
            onClick={handleSave} disabled={saving}
          >{saving ? '저장 중…' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}

// V70 QA 신규 기능 #2 (2026-10-22) — 게시판 모달.
//   list → 상세 보기 토글. HTML 렌더에 RichContentViewer sanitize 재사용.
//   V70 QA 통합 공지 (2026-10-23) — 더 이상 사용 안함 (BoardPostsModal 호출 제거).
//   코드 자체는 보존 (deprecated 상태) — 데이터 참조 가능성 유지.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function BoardPostsModal({ sourceFormId, onClose }: { sourceFormId: string; onClose: () => void }) {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState<any | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fns = getFunctions();
        const r = await httpsCallable<{ sourceFormId: string }, { posts: any[] }>(fns, 'listBoardPosts')({ sourceFormId });
        if (!cancelled) setPosts(r.data.posts || []);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [sourceFormId]);

  const openPost = async (postId: string) => {
    try {
      const fns = getFunctions();
      const r = await httpsCallable<{ postId: string }, { post: any }>(fns, 'getBoardPost')({ postId });
      setSelectedPost(r.data.post);
    } catch { /* noop */ }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 8, maxWidth: 720, width: '100%', maxHeight: '85vh', overflow: 'auto', padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {selectedPost ? '📢 ' + selectedPost.title : '📢 공지사항'}
          </h3>
          <div style={{ display: 'flex', gap: 6 }}>
            {selectedPost && <button style={{ padding: '4px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 12 }} onClick={() => setSelectedPost(null)}>← 목록</button>}
            <button style={{ padding: '4px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 12 }} onClick={onClose}>닫기</button>
          </div>
        </div>
        {!selectedPost && (
          <>
            {loading && <div style={{ color: '#64748b', fontSize: 13 }}>로딩 중…</div>}
            {!loading && posts.length === 0 && <div style={{ color: '#64748b', fontSize: 13 }}>등록된 공지사항이 없습니다.</div>}
            {!loading && posts.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {posts.map((p) => (
                  <li key={p.postId} style={{ padding: '10px 8px', borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }} onClick={() => openPost(p.postId)}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{p.title}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      {tournamentAuthorLabel(p.createdByName)} · {new Date(tsToMs(p.createdAt)).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {selectedPost && (
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
              {tournamentAuthorLabel(selectedPost.createdByName)} · {new Date(tsToMs(selectedPost.createdAt)).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
            </div>
            {/* V70 QA 리오픈 #10/#12 (2026-10-22) — RichContentViewer 재사용으로 ul/ol/table/img 스타일 자동 적용.
                  이전엔 className="rich-content-viewer" 만 지정 → 스타일 블록(<style>) 미로드로 불릿/번호/표/이미지 깨짐. */}
            <RichContentViewer content={selectedPost.contentHtml || ''} format="html" style={{ fontSize: 13, lineHeight: 1.6 }} />
          </div>
        )}
      </div>
    </div>
  );
}

// V70-K — 게임대회 전용 배경 (본문 영역 끝까지). V70-L — 대시보드만 풀 다크 배경 #0D0F16 (시범)
const pageStyle: React.CSSProperties = { ...tournamentPageStyle({ x: 16, y: 20 }), background: DARK.page };
const badge: React.CSSProperties = {
  display: 'inline-block', padding: '2px 10px', borderRadius: 10,
  fontSize: 11, fontWeight: 700, border: 'none',
};
const errorBox: React.CSSProperties = {
  padding: 10, color: '#991b1b', background: '#fee2e2',
  border: '1px solid #fecaca', borderRadius: 6, fontSize: 13, marginBottom: 12,
};
// V70-L — 대진표 섹션 빈 상태·로딩 (다크)
const placeholderStyle: React.CSSProperties = darkEmptyBox;
// GC-DASH-01 (2026-10-23) — "대진표 보기" 섹션 이미지 중심 카드 스타일 토큰.
// V70-G — 카드 최소 폭 240px → 280px. 본문 최대 폭 1080px 기준 PC 최대 3열(기존 최대 4열),
//   본문 폭 868px 미만 2열, 574px 미만 1열. 사이드바 폭과 무관하게 실제 본문 폭으로 열 수가 정해짐.
//   min(280px, 100%) — 본문이 280px 보다 좁아도 카드가 넘치지 않음.
export const BRACKET_GRID_COLUMNS = 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))';
const bracketGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: BRACKET_GRID_COLUMNS, gap: 14,
};
// V70-L — 다크 카드 (명도 차이로 배경과 구분)
const bracketCardStyle: React.CSSProperties = {
  background: DARK.card, border: `1px solid ${DARK.border}`, borderRadius: 12,
  cursor: 'pointer', transition: 'box-shadow 150ms, transform 150ms',
  overflow: 'hidden',
};
const bracketCardImageWrap: React.CSSProperties = {
  position: 'relative', width: '100%', aspectRatio: '16 / 9',
  background: DARK.surface, overflow: 'hidden',
};
const bracketCardImageStyle: React.CSSProperties = {
  width: '100%', height: '100%', objectFit: 'cover', display: 'block',
};
const bracketCardPlaceholderStyle: React.CSSProperties = {
  width: '100%', height: '100%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: `linear-gradient(135deg, ${DARK.surface} 0%, ${DARK.card} 100%)`,
  color: DARK.textMuted,
};

export default TournamentDashboardPage;
